/**
 * Admin JWT auth flow — login / refresh / logout
 * =================================================================
 *
 * Contract (divinheal-admin /quantum/business/auth/login):
 *   Request  : { email, password, otp? }
 *   Response : { access_token, refresh_token, expires_in, user_role }
 *
 * 认证必须走真实服务端接口；前端不提供本地伪登录或接口未实现回退。
 *
 * Token 存储策略：
 *   - access_token  → localStorage["admin_jwt_access_token"]
 *   - refresh_token → localStorage["admin_jwt_refresh_token"]
 *   - expires_at    → localStorage["admin_jwt_expires_at"]  (ms epoch)
 *   - 兼容现有 quantum_token（旧 SMS-OTP flow 写入）— getAccessToken()
 *     在新 key 缺失时 fallback 读旧 key，避免破坏 T-DIVINHEAL-001 既有契约。
 *
 * Federation：getAccessTokenFor(service) 抽象跨子系统 token 选择。
 *   - 短期：admin / settlement 共用同一 access_token
 *   - 长期：统一 admin identity provider（见 docs/admin-jwt-federation.md）
 */
import axios, { type AxiosInstance } from "axios";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type AdminAuthService = "admin" | "settlement";

export interface AdminLoginResponse {
  access_token: string;
  refresh_token: string;
  /** access token 有效期（秒），用于本地刷新调度 */
  expires_in: number;
  user_role: string;
}

export interface AdminLoginRequest {
  email: string;
  password: string;
  /** 首次登录或敏感账号要求时携带 */
  otp?: string;
}

// ---------------------------------------------------------------------------
// Storage keys
// ---------------------------------------------------------------------------
export const STORAGE_ACCESS_TOKEN = "admin_jwt_access_token";
export const STORAGE_REFRESH_TOKEN = "admin_jwt_refresh_token";
export const STORAGE_EXPIRES_AT = "admin_jwt_expires_at";
/** 旧 SMS-OTP flow 的 token key（T-DIVINHEAL-001 之前已存在）*/
export const STORAGE_LEGACY_TOKEN = "quantum_token";

// ---------------------------------------------------------------------------
// Token read / write
// ---------------------------------------------------------------------------
export function getAccessToken(): string | null {
  const fresh = localStorage.getItem(STORAGE_ACCESS_TOKEN);
  if (fresh) return fresh;
  // legacy fallback
  return localStorage.getItem(STORAGE_LEGACY_TOKEN);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(STORAGE_REFRESH_TOKEN);
}

export function getAccessTokenExpiresAt(): number | null {
  const raw = localStorage.getItem(STORAGE_EXPIRES_AT);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * 跨子系统 token 选择
 *
 * 短期：admin / settlement 共用一份 access_token（两端各自验签）
 * 长期：当统一 admin identity provider 落地后，此函数按 service 路由不同 token
 */
export function getAccessTokenFor(_service: AdminAuthService): string | null {
  // 短期实现：两个 service 共用 access_token
  return getAccessToken();
}

export function setTokens(payload: AdminLoginResponse): void {
  localStorage.setItem(STORAGE_ACCESS_TOKEN, payload.access_token);
  localStorage.setItem(STORAGE_REFRESH_TOKEN, payload.refresh_token);
  const expiresAt = Date.now() + payload.expires_in * 1000;
  localStorage.setItem(STORAGE_EXPIRES_AT, String(expiresAt));
  // 同步 legacy key —— 这样既有的 lib/api.ts JWT 注入拦截器
  // （读 quantum_token）能继续工作，避免一次性破坏所有调用方
  localStorage.setItem(STORAGE_LEGACY_TOKEN, payload.access_token);
}

export function clearTokens(): void {
  localStorage.removeItem(STORAGE_ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_EXPIRES_AT);
  localStorage.removeItem(STORAGE_LEGACY_TOKEN);
}

// ---------------------------------------------------------------------------
// HTTP client (独立 axios 实例，避免循环依赖 lib/api.ts)
// ---------------------------------------------------------------------------
let authClient: AxiosInstance | null = null;
function getAuthClient(): AxiosInstance {
  if (!authClient) {
    authClient = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
      headers: { "Content-Type": "application/json" },
      // refresh 请求超时收紧，避免 401 hang 导致界面卡死
      timeout: 10_000,
    });
  }
  return authClient;
}

/** 仅测试用：注入自定义 axios 实例 */
export function __setAuthClientForTest(client: AxiosInstance | null): void {
  authClient = client;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
const LOGIN_ENDPOINT = "/quantum/business/auth/login";
const REFRESH_ENDPOINT = "/quantum/business/auth/refresh";

function unwrapAdminLoginResponse(raw: unknown): AdminLoginResponse {
  const data =
    raw &&
    typeof raw === "object" &&
    "data" in raw &&
    (raw as { data?: unknown }).data &&
    typeof (raw as { data?: unknown }).data === "object" &&
    "access_token" in ((raw as { data: unknown }).data as Record<string, unknown>)
      ? (raw as { data: AdminLoginResponse }).data
      : (raw as AdminLoginResponse);
  if (
    !data ||
    typeof data.access_token !== "string" ||
    typeof data.refresh_token !== "string"
  ) {
    throw new Error("MALFORMED_AUTH_RESPONSE");
  }
  return data;
}

/** 发起真实 admin 登录请求。 */
export async function loginRequest(
  email: string,
  password: string,
  otp?: string
): Promise<AdminLoginResponse> {
  const body: AdminLoginRequest = { email, password };
  if (otp) body.otp = otp;

  const res = await getAuthClient().post<AdminLoginResponse>(LOGIN_ENDPOINT, body);
  return unwrapAdminLoginResponse(res.data);
}

/**
 * 用 refresh_token 换新 access_token
 *
 * 失败时调用方应清 token 并跳登录；本函数仅负责发请求 + 返回 / 抛错。
 */
export async function refreshRequest(): Promise<AdminLoginResponse> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error("NO_REFRESH_TOKEN");
  }

  const res = await getAuthClient().post<AdminLoginResponse>(REFRESH_ENDPOINT, {
    refresh_token: refreshToken,
  });
  return unwrapAdminLoginResponse(res.data);
}

export async function logoutRequest(): Promise<void> {
  // logout 不发后端调用（无副作用风险）；仅清本地状态
  clearTokens();
  localStorage.removeItem("quantum_admin");
  localStorage.removeItem("quantum_module");
}
